import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const GeneroSelect = ({ value, onChange }) => (
  <div>
    <Label>Genere</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="register-genere-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
      <SelectContent>
        {["Uomo", "Donna", "Non indicato"].map((g) => <SelectItem key={g} value={g} data-testid={`register-genere-option-${g.toLowerCase().replace(" ", "-")}`}>{g}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const FIELDS = [
  ["nome", "Nome", "text"],
  ["cognome", "Cognome", "text"],
  ["data_nascita", "Data di nascita", "date"],
  ["luogo_nascita", "Luogo di nascita", "text"],
  ["email", "Email", "email"],
  ["indirizzo", "Indirizzo", "text", true],
  ["telefono", "Recapito telefonico", "tel"],
  ["tessera_fiv", "Numero tessera FIV", "text"],
  ["username", "Login (nome utente)", "text"],
  ["password", "Password (min. 8 caratteri)", "password"],
  ["password2", "Conferma password", "password"],
];

export default function Register() {
  const { register } = useAuth();
  const [doneEmail, setDoneEmail] = useState(null);
  const [f, setF] = useState({ ...Object.fromEntries(FIELDS.map(([k]) => [k, ""])), genere: "" });
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!f.genere) return toast.error("Seleziona il genere");
    if (f.password !== f.password2) return toast.error("Le password non coincidono");
    setConfirmOpen(true);
  };

  const send = async () => {
    setConfirmOpen(false);
    setLoading(true);
    const { password2, ...payload } = f;
    try {
      const data = await register(payload);
      setDoneEmail(data.email);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (doneEmail) {
    return (
      <AuthShell title="Controlla la tua email" subtitle="Manca solo un passaggio.">
        <div className="space-y-4" data-testid="register-done">
          <p>Abbiamo inviato un link di conferma a <strong>{doneEmail}</strong>. Aprilo per attivare l'account (valido 48 ore), poi accedi e associati per l'anno in corso.</p>
          <Link to="/login" className="text-emerald-700 font-medium hover:underline" data-testid="register-go-login-link">Vai all'accesso</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Registrati" subtitle="Crea il tuo account per associarti e gestire la tua flotta.">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="register-form">
        {FIELDS.map(([k, label, type, wide]) => (
          <div key={k} className={wide ? "sm:col-span-2" : ""}>
            <Label>{label}</Label>
            <Input type={type} required minLength={k.startsWith("password") ? 8 : undefined} value={f[k]}
              onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`register-${k.replace("_", "-")}-input`} />
          </div>
        ))}
        <GeneroSelect value={f.genere} onChange={(v) => setF({ ...f, genere: v })} />
        <p className="sm:col-span-2 text-xs text-muted-foreground">
          Registrandoti dichiari di aver letto la <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid="register-privacy-link">privacy policy</a>.
        </p>
        <Button type="submit" disabled={loading} className="sm:col-span-2 h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="register-submit-button">
          {loading ? "Registrazione…" : "Crea account"}
        </Button>
        <p className="sm:col-span-2 text-sm text-muted-foreground">
          Hai già un account? <Link to="/login" className="text-emerald-700 font-medium hover:underline" data-testid="go-to-login-link">Accedi</Link>
        </p>
      </form>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="register-email-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>La tua email è corretta?</AlertDialogTitle>
            <AlertDialogDescription>
              Invieremo il link di attivazione dell'account a <strong className="text-foreground break-all" data-testid="register-email-confirm-value">{f.email.trim()}</strong>. Controlla che sia scritta correttamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="register-email-confirm-edit-button">Correggi email</AlertDialogCancel>
            <AlertDialogAction onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid="register-email-confirm-ok-button">Sì, è corretta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AuthShell>
  );
}
